import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { spotifyService } from '../services/spotifyService';
import { useToast } from '@chakra-ui/react';
import { saveAuthToken, getAuthToken, deleteAuthToken } from '../lib/tokenStorage';
import { useAuth } from '../contexts/AuthContext';
import { useLocation } from 'react-router-dom';

// Define the context type
interface SpotifyContextType {
  isAuthenticated: boolean;
  accessToken: string | null;
  login: () => void;
  logout: () => void;
  handleCallback: () => Promise<void>;
  tokenExpired: boolean;
  refreshToken: () => Promise<boolean>;
}

// Create context with a default value
const SpotifyContext = createContext<SpotifyContextType>({
  isAuthenticated: false,
  accessToken: null,
  tokenExpired: false,
  login: () => {
    console.warn('Spotify context not initialized');
  },
  logout: () => {
    console.warn('Spotify context not initialized');
  },
  handleCallback: async () => {
    console.warn('Spotify context not initialized');
  },
  refreshToken: async () => {
    console.warn('Spotify context not initialized');
    return false;
  },
});

// Helper function to check if current path is a player page that needs Spotify
function isPlayerPage(pathname: string): boolean {
  return pathname.includes('/player') || 
         pathname.includes('/auth/callback')
}

// Provider component
export function SpotifyProvider({ children }: { children: ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [tokenExpired, setTokenExpired] = useState(false);
  const toast = useToast();
  const { user } = useAuth();
  const location = useLocation();

  // Check if we're on a page that needs Spotify integration
  const isMusicPlayerPage = isPlayerPage(location.pathname);

  // Effect to handle token expiration - only run on player pages
  useEffect(() => {
    // Skip token checks and refreshes if not on a player page
    if (!isMusicPlayerPage) {
      console.log('Not on player page, skipping Spotify token checks');
      return;
    }

    // Check if token is already expired on mount
    if (spotifyService.isTokenExpired()) {
      console.log('Token already expired on mount, attempting refresh');
      
      // Try to refresh the token automatically on mount
      spotifyService.refreshToken().then(newToken => {
        if (newToken) {
          console.log('Token refreshed successfully on mount');
          setAccessToken(newToken);
          setIsAuthenticated(true);
          setTokenExpired(false);
          
          // Save refreshed token to Supabase if user is authenticated
          if (user) {
            const expirationTime = localStorage.getItem('spotify_token_expiration');
            const refreshToken = localStorage.getItem('spotify_refresh_token');
            
            saveAuthToken('spotify', {
              access_token: newToken,
              refresh_token: refreshToken || undefined,
              expires_at: expirationTime || undefined,
              user_identifier: user.email || user.id
            });
          }
        } else {
          console.log('Token refresh failed on mount');
          handleExpiredToken();
        }
      });
    } else {
      // If token is valid, schedule next refresh
      const expirationTimeStr = localStorage.getItem('spotify_token_expiration');
      if (expirationTimeStr) {
        const expirationTime = parseInt(expirationTimeStr);
        const now = Date.now();
        const timeUntilExpiry = Math.max(0, expirationTime - now) / 1000; // in seconds
        
        if (timeUntilExpiry > 0) {
          console.log(`Token valid, expires in ${timeUntilExpiry.toFixed(0)} seconds (${new Date(expirationTime).toLocaleTimeString()})`);
          // Schedule refresh based on current expiry time
          spotifyService.scheduleTokenRefresh(timeUntilExpiry);
        } else {
          // If time until expiry is not positive, the token is already expired
          console.log('Token is expired based on expiration time');
          spotifyService.refreshToken().then(newToken => {
            if (newToken) {
              console.log('Expired token refreshed successfully');
              setAccessToken(newToken);
              setIsAuthenticated(true);
              setTokenExpired(false);
              
              // Save refreshed token to Supabase if user is authenticated
              if (user) {
                const expirationTime = localStorage.getItem('spotify_token_expiration');
                const refreshToken = localStorage.getItem('spotify_refresh_token');
                
                saveAuthToken('spotify', {
                  access_token: newToken,
                  refresh_token: refreshToken || undefined,
                  expires_at: expirationTime || undefined,
                  user_identifier: user.email || user.id
                });
              }
            } else {
              handleExpiredToken();
            }
          });
        }
      }
    }

    // Listen for token expiration events
    const handleTokenExpired = () => {
      console.log('Token expired event received');
      
      // Skip refresh if not on player page anymore
      if (!isPlayerPage(window.location.pathname)) {
        console.log('Not on player page anymore, skipping token refresh');
        return;
      }
      
      // Try to refresh the token first
      spotifyService.refreshToken().then(newToken => {
        if (newToken) {
          console.log('Token refreshed successfully after expiration event');
          setAccessToken(newToken);
          setIsAuthenticated(true);
          setTokenExpired(false);
          
          // Save refreshed token to Supabase if user is authenticated
          if (user) {
            const expirationTime = localStorage.getItem('spotify_token_expiration');
            const refreshToken = localStorage.getItem('spotify_refresh_token');
            
            saveAuthToken('spotify', {
              access_token: newToken,
              refresh_token: refreshToken || undefined,
              expires_at: expirationTime || undefined,
              user_identifier: user.email || user.id
            });
          }
          
          // Show toast for successful refresh with cooldown
          if (spotifyService.shouldShowToast()) {
            toast({
              title: 'Spotify session refreshed',
              description: 'Your session has been automatically renewed.',
              status: 'success',
              duration: 3000,
              isClosable: true,
            });
          }
        } else {
          console.log('Token refresh failed after expiration event');
          handleExpiredToken();
        }
      });
    };

    // Function to handle expired token
    const handleExpiredToken = () => {
      setAccessToken(null);
      setIsAuthenticated(false);
      setTokenExpired(true);
      
      // Show a toast notification only if on player page, with cooldown
      if (isPlayerPage(window.location.pathname) && spotifyService.shouldShowToast()) {
        toast({
          title: 'Spotify session expired',
          description: 'Please log in again to continue using Spotify features.',
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
      }
    };

    // Get the token expired event name from the service
    const TOKEN_EXPIRED_EVENT = spotifyService.getTokenExpiredEvent();
    window.addEventListener(TOKEN_EXPIRED_EVENT, handleTokenExpired);

    return () => {
      window.removeEventListener(TOKEN_EXPIRED_EVENT, handleTokenExpired);
    };
  }, [toast, user, isMusicPlayerPage]);

  // Load tokens from Supabase or localStorage on mount - only for player pages
  useEffect(() => {
    // Skip token loading if not on a player page
    if (!isMusicPlayerPage) {
      return;
    }
    
    const loadToken = async () => {
      // Get local token and expiration time
      const localToken = localStorage.getItem('spotify_access_token');
      const localRefreshToken = localStorage.getItem('spotify_refresh_token');
      const localExpiration = localStorage.getItem('spotify_token_expiration');
      
      let useLocalToken = false;
      let supabaseTokenData = null;
      
      // If user is logged in, try loading from Supabase
      if (user) {
        try {
          console.log('Attempting to load Spotify token from Supabase');
          supabaseTokenData = await getAuthToken('spotify');
          
          if (supabaseTokenData && supabaseTokenData.access_token) {
            console.log('Found Spotify token in Supabase');
            
            // Compare token timestamps if both exist
            if (localToken && localExpiration && supabaseTokenData.expires_at) {
              const localExpirationTime = parseInt(localExpiration);
              const supabaseExpirationTime = parseInt(supabaseTokenData.expires_at);
              
              // If local token expires later (is newer), use it and update Supabase
              if (localExpirationTime > supabaseExpirationTime) {
                console.log('Local Spotify token is newer than Supabase token, using local token');
                useLocalToken = true;
                
                // Update Supabase with the newer local token
                await saveAuthToken('spotify', {
                  access_token: localToken,
                  refresh_token: localRefreshToken || undefined,
                  expires_at: localExpiration,
                  user_identifier: user.email || user.id
                });
                console.log('Updated Supabase with newer local Spotify token');
              } else {
                console.log('Supabase Spotify token is newer than local token, using Supabase token');
                
                // Save Supabase token to localStorage
                localStorage.setItem('spotify_access_token', supabaseTokenData.access_token);
                
                if (supabaseTokenData.refresh_token) {
                  localStorage.setItem('spotify_refresh_token', supabaseTokenData.refresh_token);
                }
                
                if (supabaseTokenData.expires_at) {
                  localStorage.setItem('spotify_token_expiration', supabaseTokenData.expires_at);
                }
                
                setAccessToken(supabaseTokenData.access_token);
                setIsAuthenticated(true);
                return;
              }
            } else if (!localToken) {
              // No local token, use Supabase token
              localStorage.setItem('spotify_access_token', supabaseTokenData.access_token);
              
              if (supabaseTokenData.refresh_token) {
                localStorage.setItem('spotify_refresh_token', supabaseTokenData.refresh_token);
              }
              
              if (supabaseTokenData.expires_at) {
                localStorage.setItem('spotify_token_expiration', supabaseTokenData.expires_at);
              }
            }
          }
        } catch (error) {
          console.error('Error loading Spotify token from Supabase:', error);
        }
      }
      
      // Fall back to local token if Supabase token wasn't used
      if (useLocalToken || (!supabaseTokenData && localToken)) {
        console.log('Using local Spotify token');
        setAccessToken(localToken);
        setIsAuthenticated(!!localToken);
      }
    };

    loadToken();
  }, [user, isMusicPlayerPage]);

  // Function to refresh the token programmatically
  const refreshToken = async (): Promise<boolean> => {
    try {
      const newToken = await spotifyService.refreshToken();
      if (newToken) {
        console.log('Token refreshed successfully via manual refresh');
        setAccessToken(newToken);
        setIsAuthenticated(true);
        setTokenExpired(false);
        
        // Save refreshed token to Supabase if user is authenticated
        if (user) {
          const expirationTime = localStorage.getItem('spotify_token_expiration');
          const refreshToken = localStorage.getItem('spotify_refresh_token');
          
          await saveAuthToken('spotify', {
            access_token: newToken,
            refresh_token: refreshToken || undefined,
            expires_at: expirationTime || undefined,
            user_identifier: user.email || user.id
          });
        }
        
        return true;
      } else {
        console.log('Manual token refresh failed');
        setAccessToken(null);
        setIsAuthenticated(false);
        setTokenExpired(true);
        return false;
      }
    } catch (error) {
      console.error('Error during manual token refresh:', error);
      setAccessToken(null);
      setIsAuthenticated(false);
      setTokenExpired(true);
      return false;
    }
  };

  const login = () => {
    console.log('Initiating Spotify login flow');
    setTokenExpired(false); // Reset token expired flag
    spotifyService.login();
  };

  const logout = async () => {
    console.log('Logging out from Spotify');
    
    // Remove from Supabase if user is authenticated
    if (user) {
      try {
        await deleteAuthToken('spotify');
      } catch (error) {
        console.error('Error deleting Spotify token from Supabase:', error);
      }
    }
    
    // Clear from localStorage
    localStorage.removeItem('spotify_access_token');
    localStorage.removeItem('spotify_refresh_token');
    localStorage.removeItem('spotify_token_expiration');
    localStorage.removeItem('spotify_code_verifier');
    
    // Update state
    setAccessToken(null);
    setIsAuthenticated(false);
    setTokenExpired(false);
  };

  const handleCallback = async () => {
    console.log('Handling Spotify callback');
    const token = await spotifyService.handleCallback();
    if (token) {
      console.log('Successfully received Spotify token');
      setAccessToken(token);
      setIsAuthenticated(true);
      setTokenExpired(false);
      
      // Save token to Supabase if user is authenticated
      if (user) {
        const expirationTime = localStorage.getItem('spotify_token_expiration');
        const refreshToken = localStorage.getItem('spotify_refresh_token');
        
        await saveAuthToken('spotify', {
          access_token: token,
          refresh_token: refreshToken || undefined,
          expires_at: expirationTime || undefined,
          user_identifier: user.email || user.id
        });
        
        console.log('Spotify token saved to Supabase');
      }
    } else {
      console.error('Failed to get token from Spotify callback');
    }
  };

  const contextValue = {
    isAuthenticated,
    accessToken,
    tokenExpired,
    login,
    logout,
    handleCallback,
    refreshToken,
  };

  return (
    <SpotifyContext.Provider value={contextValue}>
      {children}
    </SpotifyContext.Provider>
  );
}

// Hook for using the context
export function useSpotify() {
  return useContext(SpotifyContext);
} 